import React, { useState, useEffect, useCallback, useMemo } from 'react'; // useMemo eklendi
import {
    Box,
    Container,
    Typography,
    Card,
    Stack,
    TextField,
    Button,
    MenuItem,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';

const chipStyles = {
    green: { backgroundColor: '#ECFDF3', border: '1px solid #ABEFC6', color: '#067647', fontWeight: 500 },
    grey: { backgroundColor: '#F2F4F7', border: '1px solid #EAECF0', color: '#344054', fontWeight: 500 },
};
const viewListButtonStyle = {
    backgroundColor: '#F0F9FF', color: '#026AA2', border: '1px solid #B9E6FE', '&:hover': { backgroundColor: '#E0F2FE' },
    textTransform: 'none', fontWeight: '500'
};
const redButtonStyle = {
    backgroundColor: '#D92D20', color: '#FFFFFF', '&:hover': { backgroundColor: '#B42318' }, borderRadius: '8px', textTransform: 'none', px: 2
};

export default function BoxDetailsPage() {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [locations, setLocations] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    
    // Filtreleme State'leri
    const [selectedProvince, setSelectedProvince] = useState('');
    const [selectedDistrict, setSelectedDistrict] = useState('');
    
    // Veri havuzları
    const [provinces, setProvinces] = useState([]);
    const [allDistricts, setAllDistricts] = useState([]); // Tüm ilçeleri tutan state (Edit modalı için)

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [viewPriceModalOpen, setViewPriceModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [activeTab, setActiveTab] = useState('pricing');
    const [modalLoading, setModalLoading] = useState(false);
    const [locationSettings, setLocationSettings] = useState(null);
    const [newPrice, setNewPrice] = useState({ time: '', price: '', currency: 'TL' });
    const mockHours = [1, 2, 4, 6, 12, 18, 24];
    
    const [priceListData, setPriceListData] = useState([]);
    const [isPriceListLoading, setIsPriceListLoading] = useState(false);

    const fetchLocations = useCallback(async () => {
        setLoading(true);
        try {
            const response = await Services.GetDropboxLockerLocations();
            if (response.data.status && Array.isArray(response.data.result)) {
                const formattedData = response.data.result.map(loc => ({
                    id: loc.locationCode,
                    name: loc.name,
                    province: loc.cityName,
                    district: loc.districtName,
                    status: loc.locationStatus ? 'Aktif' : 'Pasif',
                    types: loc.types,
                    addressInfo: { address: loc.address }
                }));
                setLocations(formattedData);
                setFilteredRows(formattedData);

                // Benzersiz İller
                const uniqueProvinces = [...new Set(formattedData.map(item => item.province).filter(Boolean))].sort();
                setProvinces(uniqueProvinces);

                // Tüm İlçeler (Yedek ve Edit Modalı için)
                const uniqueDistricts = [...new Set(formattedData.map(item => item.district).filter(Boolean))].sort();
                setAllDistricts(uniqueDistricts);

            } else {
                toast.error("Lokasyon verisi alınamadı veya format hatalı.");
            }
        } catch (error) {
            console.error("API hatası:", error);
            toast.error("Lokasyonlar getirilirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    // --- YENİ: Dinamik İlçe Filtreleme Mantığı ---
    // Eğer bir İl seçiliyse, locations listesinden sadece o ile ait ilçeleri bulur.
    // İl seçili değilse, tüm ilçeleri (allDistricts) gösterir.
    const availableDistricts = useMemo(() => {
        if (!selectedProvince) {
            return allDistricts;
        }
        // Seçilen ile ait lokasyonları bul, onların ilçelerini al, benzersiz yap ve sırala.
        const districtsInProvince = locations
            .filter(loc => loc.province === selectedProvince)
            .map(loc => loc.district)
            .filter(Boolean);
        
        return [...new Set(districtsInProvince)].sort();
    }, [selectedProvince, locations, allDistricts]);


    const handleFilter = () => {
        let filtered = [...locations];
        if (searchTerm) {
            filtered = filtered.filter(row => row.name.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        if (selectedProvince) {
            filtered = filtered.filter(row => row.province === selectedProvince);
        }
        if (selectedDistrict) {
            filtered = filtered.filter(row => row.district === selectedDistrict);
        }
        setFilteredRows(filtered);
        setPage(1);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(1);
    };

    const handleOpenEditModal = async (row) => {
        setSelectedRow(row);
        setActiveTab('pricing');
        setEditModalOpen(true);
        setModalLoading(true);
        try {
            const response = await Services.GetLocationSettings(row.id);
            if (response.data.status) {
                setLocationSettings(response.data.result);
            } else {
                toast.error("Lokasyon ayarları getirilemedi.");
                setEditModalOpen(false);
            }
        } catch (error) {
            toast.error("Lokasyon ayarları getirilirken bir hata oluştu.");
            setEditModalOpen(false);
        } finally {
            setModalLoading(false);
        }
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedRow(null);
        setLocationSettings(null);
    };

    const handleOpenViewPriceModal = async (row) => {
        setSelectedRow(row);
        setViewPriceModalOpen(true);
        setIsPriceListLoading(true);
        try {
            const response = await Services.GetLocationSettings(row.id);
            if (response.data.status) {
                setPriceListData(response.data.result.durations);
            } else {
                toast.error("Fiyat listesi getirilemedi.");
                handleCloseViewPriceModal();
            }
        } catch (error) {
            console.error("Fiyat listesi API hatası:", error);
            toast.error("Fiyat listesi getirilirken bir hata oluştu.");
            handleCloseViewPriceModal();
        } finally {
            setIsPriceListLoading(false);
        }
    };
    
    const handleCloseViewPriceModal = () => {
        setViewPriceModalOpen(false);
        setPriceListData([]);
    };

    const handleUpdateSettings = async () => {
        if (!locationSettings) return;
        setIsUpdating(true);
        try {
            const payload = {
                ...locationSettings,
                durations: JSON.stringify(locationSettings.durations),
            };
            const response = await Services.updateLocationSettings(payload);
            if (response.data.status) {
                toast.success("Lokasyon ayarları başarıyla güncellendi!");
                handleCloseEditModal();
                fetchLocations();
            } else {
                toast.error("Güncelleme başarısız oldu.");
            }
        } catch (error) {
            toast.error("Güncelleme sırasında bir hata oluştu.");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setLocationSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleAddPrice = () => {
        if (newPrice.time && newPrice.price) {
            const timeValue = Number(newPrice.time);
            const newDurationEntry = {
                status: true,
                name: `${timeValue} ${timeValue === 1 ? 'Hour' : 'Hours'}`,
                time: timeValue,
                price: newPrice.price
            };

            setLocationSettings(prev => {
                const updatedDurations = [...prev.durations, newDurationEntry];
                const reIndexedDurations = updatedDurations.map((duration, index) => ({
                    ...duration,
                    value: index
                }));
                return { ...prev, durations: reIndexedDurations };
            });
            setNewPrice({ time: '', price: '', currency: 'TL' });
        }
    };

    const handleRemovePrice = (valueToRemove) => {
        setLocationSettings(prev => {            
            const updatedDurations = prev.durations.filter((item) => item.value !== valueToRemove);                        
            const reIndexedDurations = updatedDurations.map((duration, index) => ({
                ...duration,
                value: index
            }));
            return { ...prev, durations: reIndexedDurations };
        });
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
    }

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 4, maxWidth: '1720px' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight="bold">Dolap Detayları</Typography>
            </Stack>
            <Card sx={{ boxShadow: 'none', border: '1px solid #EAECF0' }}>
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} justifyContent="space-between">
                        <Stack direction="row" spacing={1.5} flexGrow={1}>
                            <TextField 
                                size="small" 
                                placeholder="Avm adı arayın..." 
                                value={searchTerm} 
                                onChange={e => setSearchTerm(e.target.value)} 
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} 
                                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>) }} 
                            />
                            
                            {/* İL SEÇİMİ */}
                            <TextField 
                                select 
                                label="İl Seçin" 
                                value={selectedProvince} 
                                onChange={(e) => {
                                    setSelectedProvince(e.target.value);
                                    setSelectedDistrict(''); // İl değişirse ilçe seçimini sıfırla
                                }} 
                                size="small" 
                                sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} 
                                displayEmpty
                            >
                                <MenuItem value="">Tüm İller</MenuItem>
                                {provinces.map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
                            </TextField>

                            {/* İLÇE SEÇİMİ - GÜNCELLENDİ: availableDistricts kullanıyor */}
                            <TextField 
                                select 
                                label="İlçe Seçin" 
                                value={selectedDistrict} 
                                onChange={e => setSelectedDistrict(e.target.value)} 
                                size="small" 
                                sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} 
                                displayEmpty
                            >
                                <MenuItem value="">Tüm İlçeler</MenuItem>
                                {availableDistricts.map((d) => (<MenuItem key={d} value={d}>{d}</MenuItem>))}
                            </TextField>
                            
                            <Button variant="contained" sx={redButtonStyle} onClick={handleFilter}>Filtrele</Button>
                        </Stack>
                    </Stack>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#F9FAFB' }}><TableRow><TableCell>NO</TableCell><TableCell>AVM / PLAZA ADI</TableCell><TableCell>İL</TableCell><TableCell>İLÇE</TableCell><TableCell>DOLAP TİPLERİ</TableCell><TableCell>NOKTA DURUMU</TableCell><TableCell>DÜZENLE</TableCell></TableRow></TableHead>
                        <TableBody>
                            {filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage).map((row, index) => (
                                <TableRow key={row.id}>
                                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                                    <TableCell>{row.province}</TableCell><TableCell>{row.district}</TableCell>
                                    <TableCell>
                                        <Button sx={viewListButtonStyle} onClick={() => handleOpenViewPriceModal(row)}>Listeyi görüntüle</Button>
                                    </TableCell>
                                    <TableCell><Chip label={row.status} size="small" sx={row.status === 'Aktif' ? chipStyles.green : chipStyles.grey} /></TableCell>
                                    <TableCell><IconButton onClick={() => handleOpenEditModal(row)}><EditOutlinedIcon /></IconButton></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">Sayfa başına satır:</Typography>
                        <TextField
                            select
                            size="small"
                            value={rowsPerPage}
                            onChange={handleChangeRowsPerPage}
                            sx={{ '& .MuiInputBase-root': { borderRadius: '6px' }, width: '70px' }}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                        </TextField>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Toplam {filteredRows.length} kayıttan {filteredRows.length > 0 ? (page - 1) * rowsPerPage + 1 : 0}–{Math.min(page * rowsPerPage, filteredRows.length)} arası gösteriliyor
                        </Typography>
                        <Pagination 
                            count={Math.ceil(filteredRows.length / rowsPerPage)} 
                            page={page} 
                            onChange={(e, v) => setPage(v)} 
                            color="primary" 
                            shape="rounded" 
                        />
                    </Stack>
                </Stack>
            </Card>

            {selectedRow && (
                <Dialog open={viewPriceModalOpen} onClose={handleCloseViewPriceModal} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '12px' } }}>
                    <DialogTitle sx={{ pb: 1 }}>
                        AVM / PLAZA FİYAT LİSTESİ
                        <IconButton onClick={handleCloseViewPriceModal} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" fontWeight={600} gutterBottom>{selectedRow?.name}</Typography>
                        {isPriceListLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
                        ) : (
                            <TableContainer component={Paper} sx={{ mt: 1, border: '1px solid #EAECF0', boxShadow: 'none', borderRadius: '8px' }}>
                                <Table>
                                    <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500 }}>SAAT</TableCell>
                                            <TableCell sx={{ fontWeight: 500 }}>FİYAT BİLGİSİ</TableCell>
                                            <TableCell sx={{ fontWeight: 500 }}>PARA BİRİMİ</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {priceListData.filter(d => d.status).map((p) => (
                                            <TableRow key={p.value} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell sx={{ fontWeight: 600 }}>{p.time === 24 ? '1 Gün' : p.time}</TableCell>
                                                <TableCell>{p.price}</TableCell>
                                                <TableCell>TL</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </DialogContent>
                </Dialog>
            )}

            {selectedRow && (
                <Dialog open={editModalOpen} onClose={handleCloseEditModal} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '12px' } }}>
                    <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1 }}><Typography variant="h6" fontWeight={600}>AVM / PLAZA BİLGİ DÜZENLEME</Typography><IconButton onClick={handleCloseEditModal} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton></DialogTitle>
                    <DialogContent sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, px: 1 }}>
                            <Typography variant="body1" fontWeight={600}>{selectedRow.name}</Typography>
                            <Stack direction="row" alignItems="center">
                                <Typography variant="body2" fontWeight={500} color="#344054">Lokasyon Durumu</Typography>
                                <Switch checked={locationSettings ? locationSettings.locationStatus : false} onChange={(e) => setLocationSettings(prev => ({ ...prev, locationStatus: e.target.checked }))} />
                            </Stack>
                        </Stack>
                        <Stack direction="row" p={0.5} sx={{ backgroundColor: '#F2F4F7', borderRadius: '8px' }}>
                            <Button fullWidth onClick={() => setActiveTab('pricing')} sx={activeTab === 'pricing' ? redButtonStyle : { color: '#344054', textTransform: 'none' }}>Fiyatlandırma</Button>
                            <Button fullWidth onClick={() => setActiveTab('address')} sx={activeTab === 'address' ? redButtonStyle : { color: '#344054', textTransform: 'none' }}>Adres Bilgisi</Button>
                        </Stack>

                        {modalLoading ? (<Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>) : (
                            locationSettings && (
                                <>
                                    {activeTab === 'pricing' && (
                                        <Box sx={{ py: 3 }}>
                                            <Typography variant="body1" fontWeight={500} color="#344054" gutterBottom>Fiyatlandırma</Typography>
                                            <Stack direction="row" spacing={2} mb={3} alignItems="center">
                                                <TextField
                                                    select
                                                    label="Saat"
                                                    size="small"
                                                    fullWidth
                                                    value={newPrice.time}
                                                    onChange={(e) => setNewPrice({ ...newPrice, time: e.target.value })}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                                >
                                                    <MenuItem value="">Saat seçin</MenuItem>
                                                    {mockHours.map(h => (
                                                        <MenuItem
                                                            key={h}
                                                            value={h}
                                                            disabled={locationSettings.durations.some(d => d.time === h)}
                                                        >
                                                            {h}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField label="Fiyat Bilgisi" size="small" fullWidth value={newPrice.price} onChange={(e) => setNewPrice({ ...newPrice, price: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                                                <TextField select label="Para Birimi" size="small" fullWidth value={newPrice.currency} onChange={(e) => setNewPrice({ ...newPrice, currency: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                                                    <MenuItem value="TL">TL</MenuItem><MenuItem value="USD">USD</MenuItem><MenuItem value="EUR">EUR</MenuItem>
                                                </TextField>
                                                <Button variant="contained" onClick={handleAddPrice} sx={{ ...redButtonStyle, whiteSpace: 'nowrap' }}>Ekle</Button>
                                            </Stack>
                                            <TableContainer component={Paper} sx={{ borderRadius: '8px', border: '1px solid #EAECF0', boxShadow: 'none' }}>
                                                <Table size="small">
                                                    <TableHead sx={{ backgroundColor: '#F9FAFB' }}><TableRow><TableCell>SAAT</TableCell><TableCell>FİYAT BİLGİSİ</TableCell><TableCell>PARA BİRİMİ</TableCell><TableCell align='right'>KALDIR</TableCell></TableRow></TableHead>
                                                    <TableBody>
                                                        {locationSettings.durations.filter(d => d.status).map((p, index) => (
                                                            <TableRow key={p.value}><TableCell>{p.time}</TableCell><TableCell>{p.price}</TableCell><TableCell>TL</TableCell><TableCell align='right'><IconButton size="small" onClick={() => handleRemovePrice(p.value)}><DeleteOutlineIcon color="error" /></IconButton></TableCell></TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}
                                    {activeTab === 'address' && (
                                        <Box sx={{ py: 3 }}>
                                            <Typography variant="body1" fontWeight={500} color="#344054" gutterBottom>Lokasyon Bilgileri</Typography>
                                            <Stack spacing={2}>
                                                <TextField name="locationName" label="AVM / Plaza İsmi" value={locationSettings.locationName} onChange={handleSettingsChange} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                                                <TextField name="city" select label="İl" value={locationSettings.city} onChange={handleSettingsChange} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>{provinces.map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}</TextField>
                                                <TextField name="region" select label="İlçe" value={locationSettings.region} onChange={handleSettingsChange} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>{allDistricts.map((d) => (<MenuItem key={d} value={d}>{d}</MenuItem>))}</TextField>
                                                <TextField name="address" label="Açık Adres" value={locationSettings.address} onChange={handleSettingsChange} multiline rows={3} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                                            </Stack>
                                        </Box>
                                    )}
                                </>
                            )
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #EAECF0' }}>
                        <Button onClick={handleCloseEditModal} variant="outlined" sx={{ width: '100%', maxWidth: '190px', ...redButtonStyle, backgroundColor: '#FFF', color: '#D92D20', border: '1px solid #D92D20', '&:hover': { backgroundColor: '#FEF3F2' } }}>Vazgeç</Button>
                        <Button onClick={handleUpdateSettings} variant="contained" disabled={isUpdating} sx={{ width: '100%', maxWidth: '190px', ...redButtonStyle }}>{isUpdating ? <CircularProgress size={24} color="inherit" /> : 'Güncelle'}</Button>
                    </DialogActions>
                </Dialog>
            )}
        </Container>
    );
}